import { UploadManager } from '@bytescale/sdk';
import { Modal, ModalBody, ModalContent, Spinner, useDisclosure } from '@heroui/react';
import {
  FC, ReactNode, useCallback, useMemo, useRef, useState,
} from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

import { useHeroToast } from '@/hooks';

import { Images, Upload } from 'lucide-react';

import { apiKey } from '../../config/utils';
import { generateUUID } from '../../utils';
import { TableEmptyStateArt } from '../TableEmptyState';
import { getCroppedBlob } from './cropImage';
import {
  BrowseButton,
  ChooseCol,
  ChooseDivider,
  ChooseRow,
  CompactBox,
  CompactDivider,
  CompactRow,
  CropActions,
  CropBar,
  CropCounter,
  CropStage,
  DropArea,
  DropDescription,
  DropFormats,
  DropTitle,
  GhostButton,
  HiddenInput,
  LibraryArt,
  PrimaryButton,
  UploadingOverlay,
  ZoomRange,
} from './ImageUpload.styled';
import { MediaLibraryPicker } from './MediaLibraryPicker';

// Minimalny podzbiór opcji Bytescale — pozwala przekazać istniejące obiekty opcji
// (baseForCoverImage / baseForGallery itd.) bez zmian, z konfiguracją kadrowania i trybem multi.
interface UploadOptionsLike {
  path: { folderPath: string; fileName?: string };
  mimeTypes?: string[];
  maxFileSizeBytes?: number;
  maxFileCount?: number;
  multi?: boolean;
  editor?: {
    images?: {
      crop?: boolean;
      cropRatio?: number;
      cropShape?: 'rect' | 'circ';
      preview?: boolean;
      allowResizeOnMove?: boolean;
    };
  };
}

export interface ImageUploadRenderProps {
  onClick: () => void;
}

interface ImageUploadProps {
  /** Opcje uploadu (reużywamy obiektów z config/uploadConfig — łącznie z editor/crop, multi). */
  options: UploadOptionsLike;
  /**
   * Po udanym wysłaniu — z URL-em pliku. Przy multi: raz na KAŻDY plik (chyba że podano onUploadMany).
   * Opcjonalne: galerie zwykle podają tylko `onUploadMany` (batch), pojedyncze uploady tylko `onUpload`.
   */
  onUpload?: (fileUrl: string) => void;
  /**
   * Batch po zakończeniu WSZYSTKICH plików (multi) — dostajesz komplet URL-i naraz.
   * ZALECANE dla galerii: eliminuje bug akumulacji (jedno wywołanie zamiast N ze stale-closure).
   */
  onUploadMany?: (fileUrls: string[]) => void;
  /**
   * Trigger (render-prop) — jak Bytescale `UploadButton`. PODANY → tryb MODAL (area/crop w modalu).
   * POMINIĘTY → tryb INLINE (area renderowana od razu; zamiennik `UploadDropzone`).
   */
  children?: (props: ImageUploadRenderProps) => ReactNode;
  /** Wybór wielu plików (galeria). Domyślnie z `options.multi`. Multi = kadrujemy PO KOLEI każdy. */
  multiple?: boolean;
  /** Kompaktowy, niski wariant inline (dokumenty/PDF w formularzach) — dwa krótkie boksy zamiast dużych kafli. */
  compact?: boolean;
  /**
   * Ukrywa panel „Wybierz z biblioteki" — zostaje sam upload z dysku. Używane m.in. na
   * widoku Biblioteki mediów, gdzie wybór z tej samej biblioteki nie ma sensu.
   */
  hideLibrary?: boolean;
  width?: string;
  height?: string;
  title?: string;
  description?: string;
  /**
   * Klasa z-indexu modala uploadu (np. `z-[1250]`). Potrzebna, gdy uploader otwiera się
   * Z WNĘTRZA innego modala: domyślna warstwa HeroUI ląduje wtedy POD tamtym oknem i użytkownik
   * widzi zablokowany ekran zamiast okna wyboru pliku. Domyślnie bez nadpisania.
   */
  zIndexClass?: string;
  /**
   * Klasa z-indexu dla pickera Biblioteki mediów. Musi być WYŻSZA niż `zIndexClass`,
   * bo biblioteka otwiera się NA modalu uploadu. Domyślnie ta sama warstwa, co uploader —
   * wystarcza, dopóki uploader nie stoi sam w innym modalu.
   */
  libraryZIndexClass?: string;
}

const uploadManager = new UploadManager({ apiKey });

// Rozszerzenia obrazów, które da się sensownie skadrować na canvasie (SVG celowo pomijamy —
// wektory rasteryzują się niejednoznacznie, więc wstawiamy je bez kadrowania).
const CROPPABLE_URL_EXT = /\.(png|jpe?g|gif|webp|avif|bmp)(\?|#|$)/i;
const isCroppableImageUrl = (url: string): boolean => CROPPABLE_URL_EXT.test(url);

// Nazwa pliku z URL-a Bytescale (na potrzeby originalFileName kopii po kadrowaniu).
const fileNameFromUrl = (url: string): string => {
  const clean = url.split(/[?#]/)[0];
  const base = clean.substring(clean.lastIndexOf('/') + 1);
  try {
    return decodeURIComponent(base) || 'image';
  } catch {
    return base || 'image';
  }
};

// Lista formatów do wyświetlenia (np. „JPG · PNG · WEBP") na podstawie mimeTypes.
const formatsFromMime = (mimeTypes?: string[]): string => {
  const list = mimeTypes && mimeTypes.length
    ? mimeTypes
      .map((m) => m.split('/')[1])
      .filter(Boolean)
      .map((ext) => (ext === 'jpeg' ? 'jpg' : ext).toUpperCase())
    : ['JPG', 'PNG', 'WEBP'];
  return [...new Set(list)].join(' · ');
};

/**
 * Własny box uploadu obrazu — ilustracja + drag&drop + KADROWANIE (react-easy-crop),
 * w modalu HeroUI lub inline. Multi = kadrowanie każdego pliku PO KOLEI, a URL-e trafiają
 * naraz przez `onUploadMany`. Wysyłka przez `@bytescale/sdk`.
 */
export const ImageUpload: FC<ImageUploadProps> = ({
  options,
  onUpload,
  onUploadMany,
  children,
  multiple,
  compact = false,
  hideLibrary = false,
  width = '100%',
  height,
  title,
  description,
  zIndexClass,
  libraryZIndexClass,
}) => {
  const { t } = useTranslation();
  const { showError } = useHeroToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Kadrowanie / kolejka
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [queueInfo, setQueueInfo] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const pendingFileRef = useRef<File | null>(null);
  const croppedAreaRef = useRef<Area | null>(null);
  const queueRef = useRef<File[]>([]);
  const collectedRef = useRef<string[]>([]);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [libraryOpen, setLibraryOpen] = useState(false);
  // Gdy kadrujemy plik WYBRANY Z BIBLIOTEKI — trzyma jego oryginalny URL. Pozwala:
  //  • na „Użyj oryginału" wstawić plik bez zmian (nie tworzymy zbędnej kopii),
  //  • odróżnić ten tryb od kadrowania świeżego uploadu z dysku.
  const [libraryUrl, setLibraryUrl] = useState<string | null>(null);

  const isModal = typeof children === 'function';
  const isMulti = multiple ?? options.multi ?? false;
  const formats = useMemo(() => formatsFromMime(options.mimeTypes), [options.mimeTypes]);
  const accept = (options.mimeTypes && options.mimeTypes.join(',')) || 'image/*';
  const maxBytes = options.maxFileSizeBytes;

  const cropEnabled = options.editor?.images?.crop === true;
  const roundShape = options.editor?.images?.cropShape === 'circ';
  const aspect = options.editor?.images?.cropRatio || 1;

  const clearCropView = useCallback((): void => {
    setImageSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    pendingFileRef.current = null;
    croppedAreaRef.current = null;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setLibraryUrl(null);
  }, []);

  const emit = useCallback((urls: string[]): void => {
    if (!urls.length) return;
    if (onUploadMany) onUploadMany(urls);
    else if (onUpload) urls.forEach((u) => onUpload(u));
  }, [onUpload, onUploadMany]);

  const finishFlow = useCallback((): void => {
    const urls = collectedRef.current;
    queueRef.current = [];
    collectedRef.current = [];
    clearCropView();
    setQueueInfo({ current: 0, total: 0 });
    setIsDragging(false);
    emit(urls);
    if (isModal) onClose();
  }, [clearCropView, emit, isModal, onClose]);

  const validate = useCallback((file: File): boolean => {
    if (options.mimeTypes?.length && !options.mimeTypes.includes(file.type)) {
      showError(undefined, t('upload.unsupportedFileType'));
      return false;
    }
    if (maxBytes && file.size > maxBytes) {
      showError(`${t('upload.fileSizeLimitPrefix')} ${Math.round(maxBytes / (1024 * 1024))} MB`);
      return false;
    }
    return true;
  }, [maxBytes, options.mimeTypes, showError, t]);

  const uploadOne = useCallback(async (data: Blob, fileName: string, mime?: string): Promise<string> => {
    // Unikalna nazwa PER plik. `options.path.fileName` ma zapieczony JEDEN UUID na całą
    // partię (getMediaUploadPath woła generateUUID raz na render), więc pliki o tej samej
    // nazwie źródłowej (np. przycięte kopie) nadpisywały się i w galerii zostawał jeden.
    const ext = fileName.match(/\.[^.]+$/)?.[0] ?? '';
    const base = fileName.replace(/\.[^.]+$/, '') || 'image';
    const uniqueFileName = `${base}-${generateUUID()}${ext}`;
    const result = await uploadManager.upload({
      data,
      mime: mime || undefined,
      originalFileName: fileName,
      path: { folderPath: options.path.folderPath, fileName: uniqueFileName },
      onProgress: ({ progress: p }) => setProgress(p),
    });
    return result.fileUrl;
  }, [options.path]);

  // Przetwarza następny plik z kolejki: kadruje (jeśli obraz+crop) albo wysyła wprost.
  const processQueue = useCallback(async (): Promise<void> => {
    const file = queueRef.current.shift();
    if (!file) {
      finishFlow();
      return;
    }
    const total = collectedRef.current.length + queueRef.current.length + 1;
    const current = collectedRef.current.length + 1;
    if (cropEnabled && file.type.startsWith('image/')) {
      pendingFileRef.current = file;
      croppedAreaRef.current = null;
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setQueueInfo({ current, total });
      setImageSrc(URL.createObjectURL(file)); // pauza — user zatwierdza/pomija → advance()
    } else {
      setIsUploading(true);
      setProgress(0);
      try {
        const url = await uploadOne(file, file.name, file.type || undefined);
        collectedRef.current.push(url);
      } catch {
        showError();
      } finally {
        setIsUploading(false);
        setProgress(0);
      }
      void processQueue();
    }
  }, [cropEnabled, finishFlow, showError, uploadOne]);

  // Po zatwierdzeniu/pominięciu kadru bieżącego pliku → następny.
  const advance = useCallback((): void => {
    clearCropView();
    void processQueue();
  }, [clearCropView, processQueue]);

  const confirmCrop = useCallback(async (): Promise<void> => {
    const src = imageSrc;
    const area = croppedAreaRef.current;
    const file = pendingFileRef.current;
    if (!src || !area || !file) return;
    setIsUploading(true);
    setProgress(0);
    try {
      const blob = await getCroppedBlob(src, area, { round: roundShape, mimeType: file.type });
      const baseName = roundShape ? file.name.replace(/\.[^.]+$/, '.png') : file.name;
      // Kopia z Biblioteki mediów → prefiks CROP_COPY_ w nazwie, żeby odróżnić przycięty
      // duplikat od oryginału (upload z dysku nazwy nie zmieniamy).
      const outName = libraryUrl ? `CROP_COPY_${baseName}` : baseName;
      const url = await uploadOne(blob, outName, roundShape ? 'image/png' : (file.type || undefined));
      collectedRef.current.push(url);
      advance();
    } catch {
      showError();
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  }, [advance, imageSrc, libraryUrl, roundShape, showError, uploadOne]);

  // Wybór z Biblioteki mediów. Jeśli uploader ma włączone kadrowanie, a user wskazał
  // POJEDYNCZY obraz — przepuszczamy go przez ten sam krok kadru co upload z dysku:
  // pobieramy plik do lokalnego blob-URL (canvas nie jest wtedy „tainted" przez CORS),
  // pokazujemy Cropper, a po „Przytnij" wysyłamy przycięty obraz jako NOWĄ kopię.
  // Multi (galerie) / PDF / SVG → wstawiamy odnośniki wprost, bez kadrowania (jak dotąd).
  const handleLibrarySelect = useCallback(async (urls: string[]): Promise<void> => {
    const canCrop = cropEnabled && !isMulti && urls.length === 1 && isCroppableImageUrl(urls[0]);
    if (!canCrop) {
      emit(urls);
      if (isModal) onClose();
      return;
    }
    // UWAGA: NIE ustawiamy tu isUploading — pobieramy tylko oryginał do lokalnego bloba,
    // żeby móc go skadrować. Kopia (upload) powstaje dopiero po „Przytnij" w confirmCrop.
    try {
      const res = await fetch(urls[0]);
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      const blob = await res.blob();
      pendingFileRef.current = new File([blob], fileNameFromUrl(urls[0]), { type: blob.type || 'image/jpeg' });
      croppedAreaRef.current = null;
      collectedRef.current = [];
      queueRef.current = [];
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setQueueInfo({ current: 1, total: 1 });
      setLibraryUrl(urls[0]);
      setImageSrc(URL.createObjectURL(blob)); // → wchodzimy w krok kadrowania
    } catch {
      // CORS/sieć — nie blokujemy usera; wstawiamy oryginał bez kadrowania.
      emit(urls);
      if (isModal) onClose();
    }
  }, [cropEnabled, emit, isModal, isMulti, onClose]);

  // Rezygnacja z kadru: dla pliku z biblioteki wstawiamy oryginał (bez zbędnej kopii),
  // dla uploadu z dysku — pomijamy plik i przechodzimy dalej w kolejce.
  const cancelCrop = useCallback((): void => {
    if (libraryUrl) {
      const original = libraryUrl;
      queueRef.current = [];
      collectedRef.current = [];
      clearCropView();
      setQueueInfo({ current: 0, total: 0 });
      emit([original]);
      if (isModal) onClose();
      return;
    }
    advance();
  }, [advance, clearCropView, emit, isModal, libraryUrl, onClose]);

  const handleFiles = useCallback((fileList?: FileList | null): void => {
    if (!fileList || !fileList.length) return;
    const valid = Array.from(fileList).filter(validate);
    if (!valid.length) return;
    collectedRef.current = [];
    queueRef.current = isMulti ? valid : [valid[0]];
    void processQueue();
  }, [isMulti, processQueue, validate]);

  const openBrowser = (): void => {
    if (!isUploading) inputRef.current?.click();
  };

  const percent = progress > 0
    ? `${Math.round((progress <= 1 ? progress * 100 : progress))}%`
    : '';

  const uploadingOverlay = isUploading ? (
    <UploadingOverlay>
      <Spinner size="sm" color="primary" />
      <span>
        {t('upload.uploading', 'Przesyłanie…')}
        {percent ? ` ${percent}` : ''}
      </span>
    </UploadingOverlay>
  ) : null;

  const hiddenInput = (
    <HiddenInput
      ref={inputRef}
      type="file"
      accept={accept}
      multiple={isMulti}
      onChange={(e) => {
        handleFiles(e.target.files);
        e.target.value = '';
      }}
    />
  );

  const inCropStep = Boolean(imageSrc);

  // Czy user ruszył kadr (scroll/zoom lub przesunięcie) względem stanu wyjściowego.
  const cropModified = zoom !== 1 || crop.x !== 0 || crop.y !== 0;
  // Tryb kadrowania pliku Z BIBLIOTEKI — tylko wtedy pokazujemy „Użyj oryginału".
  const isLibraryCrop = Boolean(libraryUrl);
  // Po zmianie kadru „Użyj oryginału" traci sens → wyszarzamy, a aktywujemy „Przytnij".
  const useOriginalDisabled = isUploading || (isLibraryCrop && cropModified);
  const cropConfirmDisabled = isUploading || (isLibraryCrop && !cropModified);

  const cropContent = (
    <>
      <CropStage>
        <Cropper
          image={imageSrc as string}
          crop={crop}
          zoom={zoom}
          aspect={aspect}
          cropShape={roundShape ? 'round' : 'rect'}
          showGrid={!roundShape}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={(_area, areaPixels) => { croppedAreaRef.current = areaPixels; }}
        />
        {uploadingOverlay}
      </CropStage>
      <CropBar>
        {queueInfo.total > 1 && (
          <CropCounter>
            {queueInfo.current}
            {' / '}
            {queueInfo.total}
          </CropCounter>
        )}
        <ZoomRange
          type="range"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          disabled={isUploading}
          onChange={(e) => setZoom(Number(e.target.value))}
          aria-label={t('upload.crop', 'Przytnij')}
        />
        <CropActions>
          <PrimaryButton type="button" onClick={() => { void confirmCrop(); }} disabled={cropConfirmDisabled}>
            {t('upload.crop', 'Przytnij')}
          </PrimaryButton>
          <GhostButton type="button" onClick={cancelCrop} disabled={useOriginalDisabled}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {libraryUrl
              ? t('upload.useOriginal', 'Użyj oryginału')
              : (queueInfo.total > 1 ? t('common.skip', 'Pomiń') : t('common.cancel', 'Anuluj'))}
          </GhostButton>
        </CropActions>
      </CropBar>
    </>
  );

  // Lewy box — upload z dysku (drag&drop / wybór pliku).
  const uploadBox = (
    <DropArea
      $isDragging={isDragging}
      onClick={openBrowser}
      onDragOver={(e) => { e.preventDefault(); if (!isUploading) setIsDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        if (!isUploading) handleFiles(e.dataTransfer.files);
      }}
    >
      <TableEmptyStateArt variant="upload" height={104} />
      <DropTitle>
        {isDragging ? t('upload.dropHere', 'Upuść, aby przesłać') : (title || t('upload.boxTitle', 'Dodaj zdjęcie'))}
      </DropTitle>
      <DropDescription>
        {description || t('upload.boxDescription', 'Przeciągnij i upuść lub kliknij, aby wybrać')}
      </DropDescription>
      <DropFormats>{formats}</DropFormats>
      <BrowseButton type="button" onClick={(e) => { e.stopPropagation(); openBrowser(); }}>
        {t('common.chooseFile', 'Wybierz plik')}
      </BrowseButton>
      {hiddenInput}
      {uploadingOverlay}
    </DropArea>
  );

  // Prawy box — wybór z Biblioteki mediów (istniejące pliki, bez uploadu).
  const libraryBox = (
    <DropArea $isDragging={false} onClick={() => setLibraryOpen(true)}>
      <LibraryArt><Images size={52} strokeWidth={1.5} /></LibraryArt>
      <DropTitle>{t('upload.libraryTitle', 'Wybierz z biblioteki')}</DropTitle>
      <DropDescription>{t('upload.libraryDescription', 'Z już przesłanych plików')}</DropDescription>
      <DropFormats>&nbsp;</DropFormats>
      <BrowseButton type="button" onClick={(e) => { e.stopPropagation(); setLibraryOpen(true); }}>
        {t('upload.libraryBrowse', 'Przeglądaj bibliotekę')}
      </BrowseButton>
    </DropArea>
  );

  const chooseContent = hideLibrary ? (
    <ChooseRow>
      <ChooseCol>{uploadBox}</ChooseCol>
    </ChooseRow>
  ) : (
    <ChooseRow>
      <ChooseCol>{uploadBox}</ChooseCol>
      <ChooseDivider />
      <ChooseCol>{libraryBox}</ChooseCol>
    </ChooseRow>
  );

  // Wariant kompaktowy — dwa krótkie boksy w rzędzie (dokumenty/PDF w formularzach).
  const compactChoose = (
    <CompactRow>
      <CompactBox
        type="button"
        $isDragging={isDragging}
        onClick={openBrowser}
        onDragOver={(e) => { e.preventDefault(); if (!isUploading) setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); if (!isUploading) handleFiles(e.dataTransfer.files); }}
      >
        <Upload size={17} />
        <span>{title || t('upload.boxTitleFile', 'Prześlij dokument')}</span>
      </CompactBox>
      {!hideLibrary && (
        <>
          <CompactDivider />
          <CompactBox type="button" onClick={() => setLibraryOpen(true)}>
            <Images size={17} />
            <span>{t('upload.libraryShort', 'Z biblioteki')}</span>
          </CompactBox>
        </>
      )}
      {hiddenInput}
      {uploadingOverlay}
    </CompactRow>
  );

  // eslint-disable-next-line no-nested-ternary
  const content = inCropStep ? cropContent : (compact ? compactChoose : chooseContent);

  // Picker Biblioteki mediów (render raz; sam jest modalem-portalem). Pomijany, gdy
  // panel biblioteki jest ukryty (hideLibrary) — nie ma czego wybierać.
  const picker = hideLibrary ? null : (
    <MediaLibraryPicker
      isOpen={libraryOpen}
      onClose={() => setLibraryOpen(false)}
      multiple={isMulti}
      accept={options.mimeTypes}
      onSelect={(urls) => { setLibraryOpen(false); void handleLibrarySelect(urls); }}
      zIndexClass={libraryZIndexClass ?? zIndexClass}
    />
  );

  // Tryb INLINE — area od razu w miejscu (dropContent zawiera już hiddenInput).
  // Flex-kolumna wyśrodkowuje uploader w pionie, gdy podano stałą wysokość (bez pustej przestrzeni na dole).
  if (!isModal) {
    return (
      <div style={{ width, height, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {content}
        {picker}
      </div>
    );
  }

  // Tryb MODAL — trigger otwiera modal z area/kadrowaniem.
  return (
    <>
      {children?.({ onClick: onOpen })}
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => { if (!open) { clearCropView(); queueRef.current = []; collectedRef.current = []; onClose(); } }}
        radius="lg"
        size={inCropStep ? '2xl' : '3xl'}
        hideCloseButton={isUploading}
        backdrop="opaque"
        classNames={zIndexClass ? { wrapper: zIndexClass, backdrop: zIndexClass } : undefined}
      >
        <ModalContent>
          <ModalBody className="p-6">
            {content}
          </ModalBody>
        </ModalContent>
      </Modal>
      {picker}
    </>
  );
};
